import { computeAngle } from '../Minimap3D/MinimapMath.js';

const GlobalManagerMixin = Autodesk.Viewing.GlobalManagerMixin;
const zAxis = new THREE.Vector3(0,0,1);
const tmpTarget = new THREE.Vector3();
const FLY_TO_TIME = 0.5; // same as teleportDuration in BimWalk

const avp = Autodesk.Viewing.Private;

export default class AvatarTool {
    constructor(avatarWidget) {
        this.avatarWidget = avatarWidget;
        this.targetViewer = avatarWidget.targetViewer;
        this.transforms = avatarWidget.transforms;

        this.setGlobalManager(avatarWidget.globalManager);

        this.dragDirectionStart = this.dragDirectionStart.bind(this);
        this.dragDirectionMove  = this.dragDirectionMove.bind(this);
        this.dragDirectionEnd   = this.dragDirectionEnd.bind(this);

        this.dragPositionStart = this.dragPositionStart.bind(this);
        this.dragPositionMove  = this.dragPositionMove.bind(this);
        this.dragPositionEnd = this.dragPositionEnd.bind(this);

        this.onMouseWheel = this.onMouseWheel.bind(this);

        // when dragging view direction, we lastAngle stores the angle between +y and the line from playerPos to last mouse position.
        this.lastAngle = undefined;

        // Is avatar being dragged.
        this.isDragging = false;

        // Is avatar being rotated.
        this.isRotating = false;

        // Is avatar rotation locked
        this.isDragDirectionLocked = false;

        // Is tool active.
        this.active = false;

        // Tool name.
        this.names = ['avatar-tool'];
    }

    getNames() {
        return this.names;
    }

    getName() {
        return this.names[0];
    }

    isActive() {
        return this.active;
    }

    activate() {
        if (this.isActive()) {
            return;
        }

        this.active = true;

        this.avatarWidget.widgetUI.hitArea.addEventListener('mousedown', this.dragPositionStart);
        this.avatarWidget.widgetUI.hitArea.addEventListener('touchstart', this.dragPositionStart);
        this.avatarWidget.widgetUI.addEventListener('mousedown', this.dragDirectionStart);
        this.avatarWidget.widgetUI.addEventListener('touchstart', this.dragDirectionStart);

        if (this.avatarWidget.canvasWrap) {
            this.avatarWidget.widgetUI.hitArea.addEventListener('mousewheel', this.onMouseWheel);
            this.avatarWidget.widgetUI.hitArea.addEventListener('DOMMouseScroll', this.onMouseWheel); // Firefox
            this.avatarWidget.widgetUI.addEventListener('mousewheel', this.onMouseWheel);
            this.avatarWidget.widgetUI.addEventListener('DOMMouseScroll', this.onMouseWheel); // Firefox
        }
    }

    deactivate() {
        if (!this.isActive()) {
            return;
        }

        this.active = false;
        this.isDragDirectionLocked = false;

        this.avatarWidget.widgetUI.hitArea.removeEventListener('mousedown', this.dragPositionStart);
        this.avatarWidget.widgetUI.hitArea.removeEventListener('touchstart', this.dragPositionStart);
        this.avatarWidget.widgetUI.removeEventListener('mousedown', this.dragDirectionStart);
        this.avatarWidget.widgetUI.removeEventListener('touchstart', this.dragDirectionStart);

        if (this.avatarWidget.canvasWrap) {
            this.avatarWidget.widgetUI.hitArea.removeEventListener('mousewheel', this.onMouseWheel);
            this.avatarWidget.widgetUI.hitArea.removeEventListener('DOMMouseScroll', this.onMouseWheel); // Firefox
            this.avatarWidget.widgetUI.removeEventListener('mousewheel', this.onMouseWheel);
            this.avatarWidget.widgetUI.removeEventListener('DOMMouseScroll', this.onMouseWheel); // Firefox
        }

        this.dragPositionEnd();
        this.dragDirectionEnd();
    }

    onMouseWheel(e) {
        const newEvent = new MouseEvent(e.type, e);

        newEvent.wheelDelta = e.wheelDelta;
        newEvent.deltaY = e.deltaY;

        this.avatarWidget.canvasWrap.dispatchEvent(newEvent);

        if (newEvent.defaultPrevented) {
            e.preventDefault();
        }
    }

    // Convert event client coords to absolute (=zoom and scroll-independent) pixel-coords on the map.
    screenToMap(e, clamp) {
        if (e.type.indexOf('touch') >= 0) {
            e.clientX = e.touches[0].pageX;
            e.clientY = e.touches[0].pageY;
        }

        // get minimap pixel coords
        const clientRect = this.avatarWidget.container.getBoundingClientRect();

        // get position within map widget
        const p = new THREE.Vector2(e.clientX - clientRect.left, e.clientY - clientRect.top);

        // transform point to mapImage pixels
        this.transforms.widgetToMapPixels(p, clamp);

        return p;
    }

    dragPositionStart(e) {
        this.addWindowEventListener('mousemove', this.dragPositionMove);
        this.addWindowEventListener('mouseup',   this.dragPositionEnd);
        this.addWindowEventListener('touchmove', this.dragPositionMove);
        this.addWindowEventListener('touchend',   this.dragPositionEnd);

        e.stopPropagation();
        e.preventDefault();

        // Make sure that map doesn't scroll/follow while we are moving the avatar
        this.isDragging = true;
        this.avatarWidget.dispatchEvent({ type: Autodesk.AEC.AvatarEvents.DRAG_START_EVENT });
    }

    dragPositionMove(e) {
        // get minimap pixel coords. Clamp x/y to map extent => avoid dragging out of the map
        const {x, y} = this.screenToMap(e, true);

        const camera = this.targetViewer.impl.camera;

        // keep z the same as before
        const zWorld = camera.position.z;

        // save current pos
        const oldPosition = camera.position.clone();

        const pos3D = this.transforms.mapPixelsToWorld(x, y, zWorld);

        // move camera to new 3D position
        camera.position.copy(pos3D);

        // apply delta to the target vector
        const delta = camera.position.clone().sub(oldPosition);

        camera.target.add(delta);
        camera.target.z = zWorld;

        this.targetViewer.impl.invalidate(true, true, true);
        this.targetViewer.dispatchEvent({ type: Autodesk.Viewing.CAMERA_CHANGE_EVENT, camera });

        e.stopPropagation();

        this.avatarWidget && this.avatarWidget.dispatchEvent({ type: Autodesk.AEC.AvatarEvents.DRAG_MOVE_EVENT, camera });
    }

    dragPositionEnd(e) {
        this.removeWindowEventListener('mousemove', this.dragPositionMove);
        this.removeWindowEventListener('mouseup',   this.dragPositionEnd);
        this.removeWindowEventListener('touchmove', this.dragPositionMove);
        this.removeWindowEventListener('touchend',   this.dragPositionEnd);

        e && e.stopPropagation();

        this.isDragging = false;

        // TODO: use LMV generic analytics infrastructure instead.
        if (!this.avatarInteractionTracked) {
            this.avatarInteractionTracked = true;

            if (this.avatarWidget && this.avatarWidget.trackUsage) {
                this.avatarWidget.trackUsage({
                    avatar_interaction_tracked: true
                });
            }
        }

        this.avatarWidget && this.avatarWidget.dispatchEvent({ type: Autodesk.AEC.AvatarEvents.DRAG_END_EVENT });

        avp.analytics.track('viewer.3d_minimap.avatar', {
            action: 'Move'
        });
    }

    // Get targetViewer's camera position on map coordinates.
    getCameraPositionOnMap() {
        const pos3D = this.targetViewer.impl.camera.position;
        const p = this.transforms.worldToMapPixels(pos3D);
        return p;
    }

    dragDirectionStart(e) {
        if (this.isDragDirectionLocked) {
            return;
        }
        // remember angle between north and (playerPos, mousePos) at dragStart
        const p1 = this.getCameraPositionOnMap();
        const p2 = this.screenToMap(e);
        this.lastAngle = computeAngle(p1, p2);

        this.addWindowEventListener('mousemove', this.dragDirectionMove);
        this.addWindowEventListener('mouseup',   this.dragDirectionEnd);
        this.addWindowEventListener('touchmove', this.dragDirectionMove);
        this.addWindowEventListener('touchend',   this.dragDirectionEnd);

        e.stopPropagation();
        e.preventDefault();

        this.isRotating = true;
        this.avatarWidget && this.avatarWidget.dispatchEvent({ type: Autodesk.AEC.AvatarEvents.ROTATE_START_EVENT, angle: this.lastAngle });
    }

    dragDirectionMove(e) {
        if (this.isDragDirectionLocked) {
            return;
        }

        // compute current angle
        const p1 = this.getCameraPositionOnMap();
        const p2 = this.screenToMap(e);
        const newAngle = computeAngle(p1, p2);
        const delta = this.lastAngle - newAngle;

        // apply delta to view direction
        const camera = this.targetViewer.impl.camera;
        const viewVec = camera.target.clone().sub(camera.position);
        viewVec.applyAxisAngle(zAxis, delta);
        viewVec.add(camera.position);
        camera.target.copy(viewVec);

        // Rotate up-vector as well.
        camera.up.applyAxisAngle(zAxis, delta);

        this.targetViewer.impl.syncCamera();
        this.targetViewer.dispatchEvent({ type: Autodesk.Viewing.CAMERA_CHANGE_EVENT, camera });

        this.lastAngle = newAngle;

        e.stopPropagation();

        this.avatarWidget && this.avatarWidget.dispatchEvent({ type: Autodesk.AEC.AvatarEvents.ROTATE_MOVE_EVENT, angle: this.lastAngle });
    }

    dragDirectionEnd(e) {
        if (this.isDragDirectionLocked) {
            return;
        }

        this.removeWindowEventListener('mousemove', this.dragDirectionMove);
        this.removeWindowEventListener('mouseup',   this.dragDirectionEnd);
        this.removeWindowEventListener('touchmove', this.dragDirectionMove);
        this.removeWindowEventListener('touch',   this.dragDirectionEnd);

        e && e.stopPropagation();

        this.isRotating = false;
        this.avatarWidget && this.avatarWidget.dispatchEvent({ type: Autodesk.AEC.AvatarEvents.ROTATE_END_EVENT, angle: this.lastAngle });

        avp.analytics.track('viewer.3d_minimap.avatar', {
            action: 'Rotate'
        });
    }

    convertEventHammerToMouse(event) {
        if (event.pointers) {
            event.clientX = event.pointers[0].clientX;
            event.clientY = event.pointers[0].clientY;
        }
    }

    getCurrentLevel() {
        const levelExt = this.targetViewer.getExtension('Autodesk.AEC.LevelsExtension');
        if (!levelExt) {
            return;
        }

        return levelExt.getCurrentLevel();
    }

    handleDoubleClick(e) {
        this.convertEventHammerToMouse(e);

        // get minimap pixel coords
        const {x, y} = this.screenToMap(e);

        // preserve camera-direction
        const camera = this.targetViewer.impl.camera;
        const dir = camera.getWorldDirection(tmpTarget);

        // when coming from an overview perspective, we don't want to just see the ground afterwards
        dir.z = Math.max(dir.z, 0.0);

        let zWorld = camera.position.z;

        // choose z based on current level
        const level = this.getCurrentLevel();

        if (level) {
            // Floor plus eye height (Should be 1.80m).
            let height = level.zMin + (1.8 / this.targetViewer.model.getUnitScale());

            // In case the ceiling is lower than 1.80m, we don't want to jump accidentally to the next level,
            // So in that case, just set the camera in the middle of the floor.
            if (height >= level.zMax) {
                height = (level.zMin + level.zMax) / 2;
            }

            if (isFinite(height)) {
                zWorld = height;
            }
        }

        // Compute new camera position
        const pos3D = this.transforms.mapPixelsToWorld(x, y, zWorld);
        const target = pos3D.clone().add(dir);
        const dstView = {
            position: pos3D,
            target: target,
            up: camera.up
        };

        this.isDragging = true;

        Autodesk.Viewing.Private.flyToView(this.targetViewer, dstView, FLY_TO_TIME, () => {
            this.isDragging = false;
        });

        e.stopPropagation();
        e.preventDefault();

        avp.analytics.track('viewer.3d_minimap.avatar', {
            action: 'Teleport'
        });

        return true;
    }

    handleDoubleTap(e) {
        return this.handleDoubleClick(e);
    }
}

GlobalManagerMixin.call(AvatarTool.prototype);
